"use client";

import clsx from "clsx";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

export default function PageTransitionProgressBar() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);
  const prevUrlRef = useRef("");
  const loadingRef = useRef(false);

  const currentUrl = `${pathname}?${searchParams.toString()}`;

  const startLoading = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    loadingRef.current = true;

    setLoading(true);
    setProgress(0);

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 30) return prev + 8;
        if (prev < 60) return prev + 4;
        if (prev < 85) return prev + 1;
        return prev;
      });
    }, 200);
  }, []);

  const completeLoading = useCallback(() => {
    if (!loadingRef.current) return;
    loadingRef.current = false;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setProgress(100);
    setTimeout(() => {
      setLoading(false);
      setProgress(0);
    }, 300);
  }, []);

  // Wait for browser paint before completing — ensures page content is visible
  const stopAfterPaint = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        completeLoading();
      });
    });
  }, [completeLoading]);

  useEffect(() => {
    // --- Navigation START: link clicks ---
    const handleClick = (e) => {
      const anchor = e.target.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href === "#" || href.startsWith("#")) return;

      if (
        anchor.target === "_blank" ||
        anchor.hasAttribute("download") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:")
      )
        return;

      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;

        const target = url.pathname + url.search;
        const current = window.location.pathname + window.location.search;
        if (target !== current) {
          startLoading();
        }
      } catch {
        // Invalid URL, skip
      }
    };

    // --- Navigation START: browser back/forward ---
    const handlePopState = () => {
      startLoading();
    };

    // --- Navigation END: intercept history.pushState ---
    // Next.js App Router calls pushState when navigation commits.
    // This fires after React commit, closer to actual paint than usePathname.
    const originalPushState = history.pushState;
    history.pushState = function (...args) {
      originalPushState.apply(this, args);
      if (loadingRef.current) {
        stopAfterPaint();
      }
    };

    document.addEventListener("click", handleClick, true);
    window.addEventListener("popstate", handlePopState);

    return () => {
      document.removeEventListener("click", handleClick, true);
      window.removeEventListener("popstate", handlePopState);
      history.pushState = originalPushState;
    };
  }, [startLoading, stopAfterPaint]);

  // Navigation END fallback: catches back/forward and edge cases
  useEffect(() => {
    if (prevUrlRef.current && prevUrlRef.current !== currentUrl) {
      stopAfterPaint();
    }
    prevUrlRef.current = currentUrl;
  }, [currentUrl, stopAfterPaint]);

  // Safety timeout: force stop if navigation stalls (10s)
  useEffect(() => {
    if (!loading) return;
    const timeout = setTimeout(() => {
      completeLoading();
    }, 10000);
    return () => clearTimeout(timeout);
  }, [loading, completeLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div
      data-name-progress-bar
      className={clsx(
        "bg-primary fixed top-0 left-0 z-[99] h-1 transition-all duration-300",
        {
          "opacity-100": loading,
          "opacity-0": !loading,
        },
      )}
      style={{ width: `${progress}%` }}
    />
  );
}
